/**
 * <cart-sold-out>
 *
 * Display-only sloj za rasprodate artikle u korpi. Renderuje se van #cartData /
 * checkout forme, u drawer-u (main-cart_drawer) i na cart page-u (main-cart_items).
 *
 * Problem koji rešava: Shopify tiho izbaci sold-out line item-e pri rekalkulaciji
 * korpe (svaki add/remove), pa nestanu iz sekcije posle prve izmene — iako na
 * inicijalnom reload-u postoje. Ovaj element snapshot-uje server-render-ovanu
 * sold-out karticu u localStorage dok je Shopify još drži, i re-injektuje je
 * kada je Shopify izbaci, tako da ostaje u korpi (vizuelno) do eksplicitnog
 * Remove / Save for later ili dok se artikal ne vrati kao dostupan.
 *
 * Sinhronizacija: reconcile() na connectedCallback (element se re-kreira na
 * svaki section re-render) + na `cart:refreshed` (belt-and-suspenders).
 *
 * Loaded iz sections/main-cart_drawer.liquid i sections/main-cart_items.liquid.
 */

import { addFavorite } from '@theme/favorites-storage';
import {
  getSoldOut,
  upsertSoldOut,
  removeSoldOut,
} from '@theme/cart-sold-out-storage';

class CartSoldOut extends HTMLElement {
  #onRefreshed = null;
  #removeAbortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClickCapture, true);

    this.#onRefreshed = () => this.reconcile();
    document.addEventListener('cart:refreshed', this.#onRefreshed);

    this.reconcile();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClickCapture, true);
    document.removeEventListener('cart:refreshed', this.#onRefreshed);
    this.#onRefreshed = null;
    this.#removeAbortController?.abort();
    this.#removeAbortController = null;
  }

  #getItemsContainer() {
    return this.querySelector('[data-sold-out-items]');
  }

  #getVariantId(card) {
    return (
      card.getAttribute('data-variant-id') ||
      card.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
      ''
    );
  }

  /** Očisti tranzijentno stanje pre snapshot-a (loader, injection markeri). */
  #snapshotHtml(card) {
    const clone = card.cloneNode(true);
    clone.removeAttribute('data-persisted');
    clone
      .querySelectorAll('.cart-product-card__loader.is-visible')
      .forEach((loader) => loader.classList.remove('is-visible'));
    return clone.outerHTML;
  }

  /** Variant id-jevi koji su TRENUTNO dostupni u Shopify korpi (u checkout formi). */
  #getAvailableVariantIds() {
    const ids = new Set();
    document
      .querySelectorAll(
        '#cartData .cart-product-card[data-variant-id], #main-cart__form .cart-product-card[data-variant-id]'
      )
      .forEach((card) => {
        const id = card.getAttribute('data-variant-id');
        if (id) ids.add(String(id));
      });
    return ids;
  }

  reconcile() {
    const container = this.#getItemsContainer();
    if (!container) return;

    // 1. Snapshot server-render-ovanih (živih) sold-out kartica.
    const liveVariantIds = new Set();
    container
      .querySelectorAll('.cart-product-card:not([data-persisted])')
      .forEach((card) => {
        const variantId = String(this.#getVariantId(card));
        if (!variantId) return;
        liveVariantIds.add(variantId);
        upsertSoldOut({
          variantId,
          key: card.getAttribute('data-line-id') || '',
          html: this.#snapshotHtml(card),
        });
      });

    // 2. Artikli koji su se vratili kao dostupni u korpi → skini iz sold-out sloja.
    const availableVariantIds = this.#getAvailableVariantIds();
    availableVariantIds.forEach((id) => removeSoldOut(id));

    // 3. Re-injektuj sačuvane kartice koje Shopify više ne drži (tiho izbačene).
    getSoldOut().forEach((entry) => {
      const variantId = String(entry.variantId);
      if (liveVariantIds.has(variantId)) return;
      if (availableVariantIds.has(variantId)) return;
      if (container.querySelector(`[data-persisted][data-variant-id="${variantId}"]`)) return;

      const template = document.createElement('div');
      template.innerHTML = entry.html;
      const node = template.firstElementChild;
      if (!node) return;

      node.setAttribute('data-persisted', '');
      node.setAttribute('data-variant-id', variantId);
      container.appendChild(node);
    });

    // 4. Vidljivost + koordinacija empty state-a (Shopify korpa prazna a ima sold-out).
    const hasCards = container.children.length > 0;
    this.hidden = !hasCards;
    this.#coordinateEmptyState(hasCards);
  }

  /**
   * Kada je Shopify korpa prazna (sve izbačeno) ali imamo sačuvane sold-out
   * kartice, prikaži sold-out sloj i sakrij "cart empty" poruku.
   */
  #coordinateEmptyState(hasCards) {
    if (!hasCards) return;

    const drawerBody = this.closest('.cart-drawer__body');
    if (drawerBody) {
      if (drawerBody.classList.contains('cart-drawer__body--empty')) {
        drawerBody.classList.remove('cart-drawer__body--empty');
        drawerBody.classList.add(
          'cart-drawer__body--filled',
          'cart-drawer__body--sold-out-only'
        );
        const emptyEl = drawerBody.querySelector('.cart-drawer__empty');
        if (emptyEl) emptyEl.hidden = true;
      }
      return;
    }

    const wrapper = this.closest('.main-cart__wrapper');
    if (wrapper?.classList.contains('main-cart__wrapper--empty')) {
      wrapper
        .querySelectorAll('[data-cart-empty-part]')
        .forEach((part) => (part.hidden = true));
    }
  }

  #getLocaleRoot() {
    return window.routes?.localeRootUrl || '';
  }

  /** Best-effort uklanjanje žive Shopify linije (no-op ako je već izbačena). */
  async #serverRemove(card) {
    const key = card.getAttribute('data-line-id');
    if (!key) return;

    this.#removeAbortController?.abort();
    this.#removeAbortController = new AbortController();
    const { signal } = this.#removeAbortController;

    try {
      await fetch(`${this.#getLocaleRoot()}/cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: key, quantity: 0 }),
        signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* stale key / offline — ignore */
    }
  }

  #requestRefresh() {
    // Prepusti vlasničkom cart komponentu (drawer / cart page) da re-render-uje
    // sekciju, pa da se count/footer/empty-state vrate u konzistentno stanje.
    this.dispatchEvent(new CustomEvent('cart:request-refresh', { bubbles: true }));
  }

  #openFavorites() {
    this.closest('cart-drawer')?.close?.();
    document.getElementById('favorites')?.open?.();
  }

  #onClickCapture = (event) => {
    const saveBtn = event.target.closest(
      '.cart-product-card__save, [data-action="save-for-later"]'
    );
    const removeBtn = event.target.closest('[data-action="remove-item"]');
    if (!saveBtn && !removeBtn) return;

    const card = event.target.closest('.cart-product-card');
    if (!card || !this.contains(card)) return;

    // Preuzmi vlasništvo nad interakcijom — spreči delegate handler-e sekcije
    // (change.js sa stale key-em bi re-injektovao karticu i delovalo bi kao da
    // Remove ne radi).
    event.preventDefault();
    event.stopPropagation();

    if (saveBtn) {
      void this.#onSaveForLater(card, saveBtn);
    } else {
      void this.#onRemove(card);
    }
  };

  async #onRemove(card) {
    const variantId = this.#getVariantId(card);
    if (variantId) removeSoldOut(variantId);

    card.querySelector('.cart-product-card__loader')?.classList.add('is-visible');
    await this.#serverRemove(card);
    card.remove();
    this.#afterMutation();
  }

  async #onSaveForLater(card, button) {
    const variantId = this.#getVariantId(card);

    let options = {};
    try {
      const raw = button.getAttribute('data-options');
      if (raw) options = JSON.parse(raw);
    } catch {
      /* keep empty */
    }

    addFavorite({
      productId: button.getAttribute('data-product-id'),
      variantId: button.getAttribute('data-variant-id') || variantId,
      handle: button.getAttribute('data-product-handle'),
      options,
      variantSelected: true,
    });

    if (variantId) removeSoldOut(variantId);

    card.querySelector('.cart-product-card__loader')?.classList.add('is-visible');
    await this.#serverRemove(card);
    card.remove();
    this.#afterMutation();
    this.#openFavorites();
  }

  #afterMutation() {
    const container = this.#getItemsContainer();
    const hasCards = Boolean(container && container.children.length > 0);
    this.hidden = !hasCards;
    this.#requestRefresh();
  }
}

if (!customElements.get('cart-sold-out')) {
  customElements.define('cart-sold-out', CartSoldOut);
}
